import { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const values = [
  { icon: "ri-lightbulb-flash-line", title: "Conocimiento", desc: "Formación de alto nivel basada en evidencia científica y sabiduría ancestral integrada." },
  { icon: "ri-shield-check-line", title: "Ética", desc: "Actuamos con integridad, responsabilidad y respeto hacia cada alumno y paciente." },
  { icon: "ri-seedling-line", title: "Evolución", desc: "Creemos en el crecimiento continuo: personal, profesional y espiritual." },
  { icon: "ri-global-line", title: "Impacto", desc: "Cada persona que formamos multiplica el bienestar en su comunidad y entorno." },
];

export default function Mission() {
  const { ref, inView } = useInView();

  return (
    <section id="mision" className="py-20 md:py-32 bg-[#F8F6F0]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-16 md:mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase">Nuestra Esencia</span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0A1F44] mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Visión, Misión y Valores
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            Guiados por un propósito claro: ser el puente entre la ciencia, la energía y la transformación humana.
          </p>
        </div>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Vision */}
          <div className={`relative bg-[#0A1F44] rounded-2xl p-8 md:p-10 overflow-hidden transition-all duration-700 delay-100 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#D4AF37]/10 -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#D4AF37]/20 text-[#D4AF37] mb-6">
              <i className="ri-eye-line text-xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nuestra Visión
            </h3>
            <p className="text-white/75 text-base leading-relaxed">
              Ser la institución líder en Latinoamérica en formación de terapias alternativas y desarrollo de conciencia, posicionando a México como referente mundial en salud integral y transformación humana.
            </p>
            <div className="mt-6 text-[#D4AF37] text-sm font-semibold italic">
              &ldquo;Accede a tu máximo potencial&rdquo;
            </div>
          </div>

          {/* Mission */}
          <div className={`relative bg-[#D4AF37] rounded-2xl p-8 md:p-10 overflow-hidden transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0A1F44]/20 text-[#0A1F44] mb-6">
              <i className="ri-focus-3-line text-xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nuestra Misión
            </h3>
            <p className="text-[#0A1F44]/80 text-base leading-relaxed">
              Transformar vidas a nivel físico, emocional y energético a través de formación profesional de excelencia en terapias alternativas, dotando a nuestros alumnos de herramientas reales para sanar y servir.
            </p>
            <div className="mt-6 text-[#0A1F44] text-sm font-semibold italic">
              &ldquo;Transformación real desde la raíz&rdquo;
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v, i) => (
            <div
              key={v.title}
              className={`bg-white rounded-xl p-6 border border-gray-100 hover:border-[#D4AF37]/40 transition-all duration-500 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${300 + i * 100}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0A1F44]/8 text-[#0A1F44] mb-4">
                <i className={`${v.icon} text-lg`}></i>
              </div>
              <h4 className="text-lg font-bold text-[#0A1F44] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {v.title}
              </h4>
              <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
